"use client";

import { useState } from "react";
import { Notice, SelectField } from "@/components/tool-kit/primitives";
import {
  defaultGroups,
  exampleGroups,
  GroupFields,
  type GroupInput,
  MIN_GROUPS,
  readGroups,
} from "@/components/tools/stats-tests/group-fields";
import {
  alphaOptions,
  ExampleButton,
} from "@/components/tools/stats-tests/kit";
import { TukeyHsdResults } from "@/components/tools/stats-tests-more/tukey-hsd-results";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tukeyHsd } from "@/lib/tool-kit/stats-tests-more/tukey-hsd";

/** English strings. A translated content file overrides these through `ui`. */
export const TUKEY_HSD_CALCULATOR_STRINGS = {
  groupName: "Group {n} name",
  groupData:
    "Group {n} data — numbers separated by commas, spaces, or new lines",
  groupDefault: "Group {n}",
  dataPlaceholder: "12.1, 11.4, 13.0, 12.6",
  valuesDetected: "one: value|other: values",
  addGroup: "Add group",
  removeGroup: "Remove last group",
  alphaLabel: "Family-wise significance level (α)",
  tryExample: "Try an example",
  exampleNames: "Control|Blue light|Red light|Full spectrum",
  exampleData:
    "12.1, 11.4, 13.0, 12.6, 11.9, 12.3|14.2, 15.1, 13.8, 14.9, 15.6, 14.4, 13.9|12.9, 13.6, 12.4, 13.1, 14.0|16.3, 17.1, 15.8, 16.9, 17.4, 16.0",
  results: "Results",
  qCritical: "Critical q",
  hsd: "HSD (equal group sizes)",
  hsdNone: "— (unequal sizes)",
  anovaF: "ANOVA F",
  anovaP: "ANOVA p-value",
  dfWithin: "Error df",
  msWithin: "MS within",
  twoGroups:
    "With two groups, Tukey's test is the same as an independent t-test with a pooled variance; it matters when you have three or more.",
  summaryReject:
    "At α = {alpha}, {count} of {total} comparisons are significant (adjusted p below α).",
  summaryNone:
    "At α = {alpha}, none of the {total} comparisons is significant (every adjusted p is at least α).",
  groupsHeading: "Group means and homogeneous groups",
  groupsHint:
    "Groups that share a letter are not significantly different. Letters run from the highest mean (a) downward.",
  group: "Group",
  n: "n",
  mean: "Mean",
  sd: "SD",
  letters: "Letters",
  pairsHeading: "Pairwise comparisons (Tukey–Kramer)",
  pairsHint:
    "Each interval is a simultaneous {level} confidence interval for the difference of two means: difference ± q_crit × SE, with q_crit = {q} on {k} means and {df} error degrees of freedom.",
  comparison: "Comparison",
  difference: "Difference",
  se: "SE",
  q: "q",
  pAdjusted: "p (adjusted)",
  pBelow: "< {p}",
  ciLow: "CI lower",
  ciHigh: "CI upper",
  d: "d (pooled SD)",
  significant: "Significant",
  yes: "Yes",
  no: "No",
  versus: "{a} − {b}",
  howCalculated: "How this was calculated",
  workingMs:
    "MS_W = SS_W / (N − k) = {ss} / {df} = {ms}, pooled from all {k} groups.",
  workingSe:
    "SE = √(MS_W / 2 × (1/nᵢ + 1/nⱼ)). For {a} and {b}: √({ms} / 2 × (1/{ni} + 1/{nj})) = {se}.",
  workingQ:
    "q = |{diff}| / {se} = {q}. With {k} means and {df} error df, the adjusted p-value is {p}.",
  workingCrit:
    "q_crit = {q} at α = {alpha}; interval half-width for this pair = q_crit × SE = {half}.",
  apaOmnibusHeading: "APA-style result: ANOVA",
  apaOmnibusTemplate: "*F*({df1}, {df2}) = {f}, *p* {p}",
  apaHeading: "APA-style result: Tukey comparisons",
  apaPairTemplate:
    "{pair}: *M*diff = {diff}, {level}% CI [{lo}, {hi}], *p* {p}",
  apaNote:
    "Report the group means and SDs, and say the comparisons are Tukey HSD (Tukey–Kramer when group sizes differ). Differences are first − second.",
  copyApa: "Copy APA result",
  copied: "Copied!",
  prompt: "Enter at least two numbers in each of at least two groups.",
  errorVariance:
    "Every value equals its group mean, so there is no within-group variance and the comparisons are undefined.",
  errorDf:
    "Tukey's test needs at least two error degrees of freedom (N − k); add more values.",
} as const;

const FAILURE_MESSAGES = {
  "no-variance": "errorVariance",
  "too-few-df": "errorDf",
} as const;

export default function TukeyHsdCalculatorWidget() {
  const strings = useToolUi(TUKEY_HSD_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [groups, setGroups] = useState<GroupInput[]>(() =>
    defaultGroups(strings.groupDefault, 3)
  );
  const [alpha, setAlpha] = useState("0.05");

  const { data, names } = readGroups(groups, strings.groupDefault);
  const outcome =
    data.length >= MIN_GROUPS ? tukeyHsd(data, Number(alpha)) : null;
  let notice: string = strings.prompt;
  if (outcome && !outcome.ok) {
    const key =
      FAILURE_MESSAGES[outcome.reason as keyof typeof FAILURE_MESSAGES];
    notice = key ? strings[key] : strings.prompt;
  }

  return (
    <div className="space-y-6">
      <GroupFields
        fmt={fmt}
        groups={groups}
        idPrefix="tk"
        locale={locale}
        onChange={setGroups}
        text={strings}
      />
      <div className="flex flex-wrap items-end gap-4">
        <div className="w-full max-w-xs">
          <SelectField
            id="tk-alpha"
            label={strings.alphaLabel}
            onChange={setAlpha}
            options={alphaOptions(fmt)}
            value={alpha}
          />
        </div>
        <ExampleButton
          label={strings.tryExample}
          onClick={() =>
            setGroups(exampleGroups(strings.exampleNames, strings.exampleData))
          }
        />
      </div>
      {outcome?.ok ? (
        <TukeyHsdResults
          fmt={fmt}
          names={names}
          r={outcome.result}
          strings={strings}
        />
      ) : (
        <Notice>{notice}</Notice>
      )}
    </div>
  );
}
